import { useState, useCallback, useEffect, useRef } from "react";
import { inventoryFetchClient } from "./inventoryFetchClient";
import { useStableOnError } from "./useStableOnError";
import type { InventoryInspectionOutput } from "./types";

export const useInventoryInspections = (
  propertyId: string | undefined,
  options?: { onError?: (error: Error) => void }
) => {
  const [data, setData] = useState<InventoryInspectionOutput[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const hasFailedRef = useRef(false);
  const reportError = useStableOnError(options?.onError);

  useEffect(() => {
    hasFailedRef.current = false;
    setError(null);
  }, [propertyId]);

  const fetchData = useCallback(async () => {
    if (!propertyId || hasFailedRef.current) return;

    setLoading(true);
    try {
      const result = await inventoryFetchClient<InventoryInspectionOutput[]>(
        `/properties/${propertyId}/inventory-inspections`,
        {
          method: "GET",
          operation: "listInventoryInspections",
        }
      );
      setData(result);
      setError(null);
      hasFailedRef.current = false;
    } catch (caught) {
      const err =
        caught instanceof Error ? caught : new Error("Failed to load inventories");
      hasFailedRef.current = true;
      setError(err);
      reportError(err);
    } finally {
      setLoading(false);
    }
  }, [propertyId, reportError]);

  useEffect(() => {
    if (!propertyId) {
      setLoading(false);
      return;
    }
    void fetchData();
  }, [propertyId, fetchData]);

  const refetch = useCallback(() => {
    hasFailedRef.current = false;
    setError(null);
    return fetchData();
  }, [fetchData]);

  return { data, loading, error, refetch };
};
